import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@material-ui/core';
import { FiX } from 'react-icons/fi';
import { List as ListLoader } from 'react-content-loader';
import { format, parseISO } from 'date-fns';

import api from '../../../services/api';
import { errorMessage$ } from '../../../utils/subjects';
import PendingProjectInfo from './PendingProjectInfo';

// O selo é o que foi vendido. O caminho (Create/Attach) é decisão do staffing e
// aparece no painel, como Path.
function orderBadge(order) {
  return order.IsExtension ? 'Extension' : 'New project';
}

// Ordens de projeto vindas do inclient, aguardando o staffing. Abre no lugar do
// "Create project" para Allocation/Admin com a flag ligada; criar sem venda segue
// possível pelo link do rodapé, que abre o DialogProject de sempre.
export default function DrawerPendingProjects({
  open,
  handleClose,
  onCreateWithoutSale,
}) {
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [selectedId, setSelectedId] = useState(null);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('scheduling/project-orders');
      setOrders(response.data || []);
    } catch (err) {
      errorMessage$.next({ open: true, message: 'Error loading pending projects' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) loadOrders();
  }, [open, loadOrders]);

  const handleCloseInfo = useCallback(() => {
    setSelectedId(null);
    loadOrders();
  }, [loadOrders]);

  return (
    <Drawer anchor="right" open={open} onClose={handleClose}>
      <Box m={2} width={420} display="flex" flexDirection="column" height="100%">
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h5" component="h5" style={{ fontWeight: 500 }}>
            Sold projects to review
          </Typography>
          <IconButton size="small" onClick={handleClose} aria-label="Close">
            <FiX />
          </IconButton>
        </Box>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Sold in inclient and waiting for staffing validation
        </Typography>

        <Box flex={1} mt={1}>
          {loading ? (
            <ListLoader speed={2} />
          ) : orders.length === 0 ? (
            <Typography variant="body2" color="textSecondary">
              No sold projects waiting for validation.
            </Typography>
          ) : (
            <List disablePadding>
              {orders.map(order => (
                <ListItem
                  key={order.ID}
                  button
                  divider
                  onClick={() => setSelectedId(order.ID)}
                >
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                        <span>
                          {`${order.ProjectCode || '(no code)'} — ${
                            order.ProjectName || '(no name)'
                          }`}
                        </span>
                        <Chip
                          size="small"
                          label={orderBadge(order)}
                          color={order.IsExtension ? 'secondary' : 'default'}
                        />
                      </Box>
                    }
                    secondary={[
                      order.ClientName,
                      order.BusinessUnitName,
                      order.CreatedAt &&
                        format(parseISO(order.CreatedAt), 'dd MMM yyyy'),
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>

        <Divider />
        <Box mt={1} textAlign="center">
          <Button size="small" color="default" onClick={onCreateWithoutSale}>
            Create a project without a sale
          </Button>
        </Box>
      </Box>

      {selectedId && (
        <PendingProjectInfo
          id={selectedId}
          open={!!selectedId}
          handleClose={handleCloseInfo}
        />
      )}
    </Drawer>
  );
}
